import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '../../../../components/ui/Card.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input, FormField } from '../../../../components/ui/FormField.js';
import { StatusBadge } from '../../../../components/ui/StatusBadge.js';
import { Money } from '../../../../components/ui/Money';
import { EntityActionMenu } from '../../../../components/ui/EntityActionMenu';
import { Search, Calculator, Receipt, Plus } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../lib/locale';
import { Decimal } from 'decimal.js';

import { TableSkeleton } from '../../../../components/ui/Skeleton';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { usePageTitle } from '../../../../lib/hooks';
import { useAdvancesList, useCreateAdvance } from '../../../../features/finance/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { useToast } from '../../../../components/ui/Toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/Dialog';
import { ChitGroupPicker, SubscriberPicker, AuctionPicker } from '../../../../components/pickers';

export const Route = createFileRoute('/_auth/finance/advances/')({
 component: AdvancesRegisterPage,
});

interface AdvanceRecord {
 id: string;
 subscriberName: string | null;
 ticketNumber: number | null;
 amountPaise: number;
 interestRateAnnualBps: number | null;
 daysPending: number;
 accruedInterestPaise: number;
 status: 'OPEN' | 'PARTIALLY_PAID' | 'FULLY_PAID';
}

const STATUS_LABEL: Record<AdvanceRecord['status'], string> = {
 OPEN: 'OPEN',
 PARTIALLY_PAID: 'PARTIALLY PAID',
 FULLY_PAID: 'FULLY PAID',
};

function AdvancesRegisterPage() {
  usePageTitle('Chit Advances');
  const navigate = useNavigate();
  const { toast } = useToast();
  const createAdvance = useCreateAdvance();
  const [query, setQuery] = React.useState('');
  const { data: response, isLoading, isError, error, refetch } = useAdvancesList(query ? { query } : undefined);
  const records = (response?.data ?? []) as AdvanceRecord[];

  const [isIssueOpen, setIsIssueOpen] = React.useState(false);
  const [issueForm, setIssueForm] = React.useState({
    schemeId: '',
    subscriptionId: '',
    auctionId: '',
    amountRupees: '',
    interestRateAnnual: '18',
    docChargeRupees: '0',
    notes: '',
  });

  const handleIssueSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!issueForm.schemeId || !issueForm.subscriptionId || !issueForm.auctionId) {
      toast({
        type: 'error',
        title: 'Validation Error',
        description: 'Please provide Scheme ID, Subscription ID, and Auction ID.',
      });
      return;
    }
    const amt = new Decimal(issueForm.amountRupees);
    if (amt.isNaN() || amt.lte(0)) {
      toast({
        type: 'error',
        title: 'Validation Error',
        description: 'Please enter a valid positive advance amount.',
      });
      return;
    }

    try {
      await createAdvance.mutateAsync({
        schemeId: issueForm.schemeId,
        subscriptionId: issueForm.subscriptionId,
        auctionId: issueForm.auctionId,
        amountPaise: amt.mul(100).round().toNumber(),
        interestRateAnnualBps: new Decimal(issueForm.interestRateAnnual || 0).mul(100).round().toNumber(),
        docChargePaise: new Decimal(issueForm.docChargeRupees || 0).mul(100).round().toNumber(),
        notes: issueForm.notes || undefined,
      });

      toast({
        type: 'success',
        title: 'Advance Issued',
        description: 'Chit bridge advance successfully issued to subscriber.',
      });
      setIsIssueOpen(false);
      setIssueForm({
        schemeId: '',
        subscriptionId: '',
        auctionId: '',
        amountRupees: '',
        interestRateAnnual: '18',
        docChargeRupees: '0',
        notes: '',
      });
      refetch();
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      toast({
        type: 'error',
        title: 'Failed to Issue Advance',
        description: err instanceof Error ? err.message : 'Could not issue advance.',
      });
    }
  };

 const [calcForm, setCalcForm] = React.useState({
   principal: '50000',
   rate: '18',
   days: '30',
   result: '739.73',
 });

 const handleCalculate = (e: React.FormEvent) => {
   e.preventDefault();
   const p = new Decimal(calcForm.principal);
   const r = new Decimal(calcForm.rate);
   const d = new Decimal(calcForm.days);

   if (p.isNaN() || r.isNaN() || d.isNaN()) return;

   const interest = p.mul(r.div(100)).mul(d.div(365));
   setCalcForm({
     ...calcForm,
     result: interest.toFixed(2),
   });
 };

 const filteredRecords = records.filter(r =>
   (r.subscriberName ?? '').toLowerCase().includes(query.toLowerCase()) ||
   String(r.ticketNumber ?? '').toLowerCase().includes(query.toLowerCase())
 );

 return (
   <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
     <PageHeader
       title="Chit Advances & Interest"
       subtitle="Track short-term bridge advances offered to subscribers, monitor accruing interest fines, and log payments."
       action={
         <Button variant="primary" size="sm" onClick={() => setIsIssueOpen(true)}>
           <Plus className="w-4 h-4 mr-1.5" />
           Issue Advance
         </Button>
       }
     />
     <div className="mb-4">
       <div className="relative max-w-sm">
         <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
         <Input
           type="text"
           placeholder="Search subscriber name or ticket..."
           value={query}
           onChange={e => setQuery(e.target.value)}
           className="w-full h-9 pl-9 pr-3"
         />
       </div>
     </div>
     <div className="flex-1 w-full min-h-0">
       <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
         <div className="lg:col-span-3 space-y-4">
           {isLoading ? (
             <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
               <TableSkeleton columns={7} rows={5} />
             </div>
           ) : isError ? (
             <div className="bg-surface-1 border border-danger-bg rounded-xl p-8 text-center shadow-sm">
               <p className="text-sm font-semibold text-danger-600">Failed to load advances</p>
               <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
               <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
             </div>
           ) : filteredRecords.length === 0 ? (
             <EmptyState
               title="No advances found"
               description={query ? 'No records match your search. Try a different query.' : 'No advance records have been created yet.'}
               icon={<Receipt className="w-8 h-8" />}
             />
           ) : (
             <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
               <table className="w-full border-collapse text-left text-sm">
                 <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                   <tr>
                     <th className="p-4">Subscriber</th>
                     <th className="p-4">Principal Advance</th>
                     <th className="p-4">Interest Rate (Annual)</th>
                     <th className="p-4">Days Overdue</th>
                     <th className="p-4">Accrued Interest</th>
                     <th className="p-4">Status</th>
                     <th className="p-4 text-right">Actions</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-border text-text-primary">
                   {filteredRecords.map(r => (
                     <tr key={r.id} className="hover:bg-surface-2 transition-colors">
                       <td className="p-4">
                         <div className="flex flex-col">
                           <span className="font-semibold text-text-primary">{r.subscriberName ?? 'Unlinked'}</span>
                           <span className="font-mono text-xs text-text-muted mt-0.5">{r.ticketNumber ?? '—'}</span>
                         </div>
                       </td>
                       <td className="p-4">
                         <Money paise={r.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                       </td>
                       <td className="p-4 font-mono">{r.interestRateAnnualBps ? `${(r.interestRateAnnualBps / 100).toFixed(2)}% p.a.` : '—'}</td>
                       <td className="p-4 font-mono text-danger-600 font-semibold">{r.daysPending} Days</td>
                       <td className="p-4 text-amber-600 font-semibold">
                         <Money paise={r.accruedInterestPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                       </td>
                       <td className="p-4">
                         <StatusBadge
                           status={r.status === 'FULLY_PAID' ? 'success' : r.status === 'OPEN' ? 'danger' : 'warning'}
                           label={STATUS_LABEL[r.status]}
                         />
                       </td>
                       <td className="p-4 text-right">
                         <EntityActionMenu
                           actions={[
                             {
                               label: 'Record Payment',
                               icon: <Receipt className="w-4 h-4" />,
                               hidden: r.status === 'FULLY_PAID',
                               onClick: () => {
                                 navigate({ to: '/finance/advances/new', search: { advanceId: r.id } as any });
                               },
                             },
                           ]}
                         />
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
           )}
         </div>

         <div className="lg:col-span-1">
           <Card className="shadow-sm">
             <CardContent className="p-5 space-y-4">
               <h3 className="font-bold text-sm text-text-primary flex items-center gap-1.5">
                 <Calculator className="w-4 h-4 text-accent-500" />
                 Interest Calculator
               </h3>
               <p className="text-xs text-text-muted">
                 Quick calculations for daily accruals on short term advances based on Indian Financial Day rules.
               </p>

               <form onSubmit={handleCalculate} className="space-y-3">
                 <FormField label="Principal (₹)">
                   <Input
                     type="number"
                     value={calcForm.principal}
                     onChange={e => setCalcForm({ ...calcForm, principal: e.target.value })}
                   />
                 </FormField>

                 <FormField label="Interest Rate (% Annual)">
                   <Input
                     type="number"
                     value={calcForm.rate}
                     onChange={e => setCalcForm({ ...calcForm, rate: e.target.value })}
                   />
                 </FormField>

                 <FormField label="Overdue Days">
                   <Input
                     type="number"
                     value={calcForm.days}
                     onChange={e => setCalcForm({ ...calcForm, days: e.target.value })}
                   />
                 </FormField>

                 <Button type="submit" variant="secondary" className="w-full">
                   Compute Interest
                 </Button>
               </form>

               <div className="bg-surface-2 p-3.5 rounded-lg border border-border text-center">
                 <span className="text-caption text-text-muted font-bold uppercase tracking-wider block">Estimated Accrual</span>
                 <span className="text-lg font-mono font-bold text-accent-600 mt-1 block">
                   <Money paise={new Decimal(calcForm.result).mul(100).round().toNumber()} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                 </span>
               </div>
             </CardContent>
           </Card>
         </div>
       </div>
     </div>
      <Dialog open={isIssueOpen} onOpenChange={setIsIssueOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Issue Bridge Advance</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleIssueSubmit} className="space-y-3.5 mt-2">
            <FormField label="Scheme" required>
              <ChitGroupPicker
                value={issueForm.schemeId}
                onChange={(val) => setIssueForm(prev => ({ ...prev, schemeId: val, subscriptionId: '', auctionId: '' }))}
                placeholder="Select Chit Scheme..."
              />
            </FormField>
            <FormField label="Subscriber / Ticket" required>
              <SubscriberPicker
                groupId={issueForm.schemeId || undefined}
                value={issueForm.subscriptionId}
                onChange={(val) => setIssueForm(prev => ({ ...prev, subscriptionId: val }))}
                placeholder={issueForm.schemeId ? "Select Subscriber / Ticket..." : "First select a scheme..."}
                disabled={!issueForm.schemeId}
              />
            </FormField>
            <FormField label="Auction" required>
              <AuctionPicker
                schemeId={issueForm.schemeId || undefined}
                value={issueForm.auctionId}
                onChange={(val) => setIssueForm(prev => ({ ...prev, auctionId: val }))}
                placeholder={issueForm.schemeId ? "Select Auction..." : "First select a scheme..."}
                disabled={!issueForm.schemeId}
              />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Advance Amount (₹)" required>
                <Input
                  type="number"
                  placeholder="e.g. 25000"
                  value={issueForm.amountRupees}
                  onChange={e => setIssueForm({ ...issueForm, amountRupees: e.target.value })}
                  required
                />
              </FormField>
              <FormField label="Interest Rate (% p.a.)">
                <Input
                  type="number"
                  placeholder="18"
                  value={issueForm.interestRateAnnual}
                  onChange={e => setIssueForm({ ...issueForm, interestRateAnnual: e.target.value })}
                />
              </FormField>
            </div>
            <FormField label="Doc Charges (₹)">
              <Input
                type="number"
                placeholder="0"
                value={issueForm.docChargeRupees}
                onChange={e => setIssueForm({ ...issueForm, docChargeRupees: e.target.value })}
              />
            </FormField>
            <FormField label="Notes">
              <Input
                placeholder="Purpose or settlement instructions"
                value={issueForm.notes}
                onChange={e => setIssueForm({ ...issueForm, notes: e.target.value })}
              />
            </FormField>
            <DialogFooter className="mt-4">
              <Button type="button" variant="secondary" onClick={() => setIsIssueOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={createAdvance.isPending}>
                {createAdvance.isPending ? 'Issuing...' : 'Issue Advance'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
   </div>
 );
}
